/**
 * Renders unit puppets as animated pixel-art 16x16 Sprites, keyed by PuppetKind.
 */

import { Container, Graphics, Sprite, Texture } from 'pixi.js';
import { PuppetKind } from './client_message_protocol';
import { GameClientWorldState } from './game_client_world_state';
import { PUPPET_SPRITES } from './puppet_sprite_definitions';
import { generatePixelTexture } from './pixel_sprite_texture_generator';

const LINE_COLORS: Record<PuppetKind, number> = {
  [PuppetKind.Transporter]: 0x44ccff,
  [PuppetKind.Combat]: 0xff4444,
};

export class RenderUnitPuppets {
  private container: Container;
  private sprites: Map<number, Sprite> = new Map();
  private animTimers: Map<number, number> = new Map();
  private animFrames: Map<number, number> = new Map();
  private frameTextures: Map<string, Texture> = new Map();
  private targetLineGfx: Graphics;

  constructor(container: Container) {
    this.container = container;
    this.targetLineGfx = new Graphics();
    this.container.addChild(this.targetLineGfx);

    for (const kind of [PuppetKind.Transporter, PuppetKind.Combat]) {
      const frames = PUPPET_SPRITES[kind];
      for (let f = 0; f < frames.length; f++) {
        const key = `${kind}_${f}`;
        this.frameTextures.set(key, generatePixelTexture(frames[f], 8));
      }
    }
  }

  sync(worldState: GameClientWorldState): void {
    const currentIds = new Set(worldState.puppets.keys());
    const now = Date.now();

    // Remove stale
    for (const [id, s] of this.sprites) {
      if (!currentIds.has(id)) {
        this.container.removeChild(s);
        s.destroy();
        this.sprites.delete(id);
        this.animTimers.delete(id);
        this.animFrames.delete(id);
      }
    }

    this.targetLineGfx.clear();

    // Add/update
    for (const p of worldState.puppets.values()) {
      let s = this.sprites.get(p.id);
      if (!s) {
        s = new Sprite();
        this.container.addChild(s);
        this.sprites.set(p.id, s);
        this.animTimers.set(p.id, now);
        this.animFrames.set(p.id, 0);
      }

      // Animation: advance frame every 300ms
      const lastTime = this.animTimers.get(p.id)!;
      let frame = this.animFrames.get(p.id)!;
      if (now - lastTime >= 300) {
        const frames = PUPPET_SPRITES[p.kind];
        frame = (frame + 1) % frames.length;
        this.animFrames.set(p.id, frame);
        this.animTimers.set(p.id, now);
      }

      const texKey = `${p.kind}_${frame}`;
      s.texture = this.frameTextures.get(texKey)!;
      s.x = p.position.x - 8;
      s.y = p.position.y - 8;

      // Target line
      if (p.target) {
        const color = LINE_COLORS[p.kind] ?? 0xffffff;
        this.targetLineGfx.moveTo(p.position.x, p.position.y);
        this.targetLineGfx.lineTo(p.target.x, p.target.y);
        this.targetLineGfx.stroke({ color, alpha: 0.3, width: 1 });
      }
    }

    // Ensure target lines render behind sprites
    if (this.targetLineGfx.parent) {
      this.container.setChildIndex(this.targetLineGfx, 0);
    }
  }
}
